import type { GbpTabId } from '@elva/ui';
import { GbpManagerView, GrowthDashboardTopBarActions } from '@elva/ui';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AsyncBoundary } from '@/components/feedback';
import { usePageTopBar } from '@/app/layouts/pageTopBar';
import { viewProps } from '@/data/viewProps';
import { useGbp, useGbpMutations } from '@/features/gbp/hooks/useGbp';

const GBP_TAB_IDS: GbpTabId[] = ['reviews', 'posts', 'qa', 'profile'];

function backendIdToUiId(
  map: Record<number, string> | undefined,
  backendId: string | null,
): number | undefined {
  if (!map || !backendId) return undefined;
  for (const [uiId, id] of Object.entries(map)) {
    if (id === backendId) return Number(uiId);
  }
  return undefined;
}

export function GbpManagerPage() {
  usePageTopBar({
    actions: <GrowthDashboardTopBarActions />,
  });

  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const initialTab = GBP_TAB_IDS.includes(tabParam as GbpTabId)
    ? (tabParam as GbpTabId)
    : undefined;
  const focusPostBackendId = searchParams.get('post');
  const focusReviewBackendId = searchParams.get('review');

  const { data, isPending, isError, error, refetch, isRefetching, isFetching } = useGbp();
  const {
    approveReview,
    approveQa,
    editReview,
    createPost,
    approvePost,
  } = useGbpMutations();
  const [pendingReviewId, setPendingReviewId] = useState<number | undefined>();
  const [pendingPostId, setPendingPostId] = useState<number | undefined>();

  const focusPostId = useMemo(
    () => backendIdToUiId(data?.postIdMap, focusPostBackendId),
    [data?.postIdMap, focusPostBackendId],
  );
  const initialOpenReviewId = useMemo(
    () => backendIdToUiId(data?.reviewIdMap, focusReviewBackendId),
    [data?.reviewIdMap, focusReviewBackendId],
  );

  const handleApproveReview = useCallback(
    (id: number) => {
      const backendId = data?.reviewIdMap[id];
      if (!backendId) return;
      setPendingReviewId(id);
      approveReview.mutate(backendId, {
        onSettled: () => setPendingReviewId(undefined),
      });
    },
    [approveReview, data?.reviewIdMap],
  );

  const handleEditReview = useCallback(
    (id: number) => {
      const backendId = data?.reviewIdMap[id];
      const review = data?.reviews.find((row) => row.id === id);
      if (!backendId || !review) return;
      const next = window.prompt('Edit response', review.draft);
      if (next == null || !next.trim()) return;
      setPendingReviewId(id);
      editReview.mutate(
        { id: backendId, responseText: next.trim() },
        { onSettled: () => setPendingReviewId(undefined) },
      );
    },
    [data?.reviewIdMap, data?.reviews, editReview],
  );

  const handleApproveQa = useCallback(
    (id: number) => {
      const backendId = data?.qaIdMap[id];
      if (!backendId) return;
      approveQa.mutate(backendId);
    },
    [approveQa, data?.qaIdMap],
  );

  const handleApprovePost = useCallback(
    (id: number) => {
      const backendId = data?.postIdMap[id];
      if (!backendId) return;
      setPendingPostId(id);
      approvePost.mutate(backendId, {
        onSettled: () => setPendingPostId(undefined),
      });
    },
    [approvePost, data?.postIdMap],
  );

  const handleCreatePost = useCallback(() => {
    const title = window.prompt('Post title');
    if (!title?.trim()) return;
    const body = window.prompt('Post body') ?? '';
    createPost.mutate({
      title: title.trim(),
      body,
      postType: 'update',
    });
  }, [createPost]);

  const actionsPending =
    approveReview.isPending ||
    editReview.isPending ||
    approveQa.isPending ||
    approvePost.isPending ||
    createPost.isPending;

  return (
    <AsyncBoundary
      loading={isPending}
      error={data === undefined && isError ? error : undefined}
      onRetry={refetch}
      retrying={isRefetching}
    >
      <GbpManagerView
        {...viewProps({
          reviews: data?.reviews,
          posts: data?.posts,
          qas: data?.qas,
          kpis: data?.kpis,
          profileItems: data?.businessName ? data.profileItems : undefined,
          profileCompletenessPct: data?.businessName ? data.profileCompletenessPct : undefined,
          photoFreshness: data?.businessName ? data.photoFreshness : undefined,
          reviewSnapshot: data?.businessName ? data.reviewSnapshot : undefined,
          offerSync: data?.offerSync,
          nextPost: data?.nextPost,
          reviewsDegraded: data?.reviewsDegraded,
          loading: isFetching || actionsPending,
          onApproveReview: handleApproveReview,
          onEditReview: handleEditReview,
          onApproveQa: handleApproveQa,
          onApprovePost: handleApprovePost,
          onCreatePost: handleCreatePost,
          pendingReviewId,
          pendingPostId,
          initialTab,
          focusPostId,
          initialOpenReviewId,
        })}
      />
    </AsyncBoundary>
  );
}
